import React, { useEffect, useState } from 'react';
import { MdDelete, MdUpload, MdEdit } from 'react-icons/md';
import { getLocation, addLocation, deleteLocation, editLocation } from 'Services/allApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';

function Location() {
    const [locations, setLocations] = useState([]);
    const [imagePreviews, setImagePreviews] = useState({});
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // State for inputs in the modal
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [mapLink, setMapLink] = useState('');
    const [address, setAddress] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [images, setImages] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await getLocation();
                if (!response.error) {
                    setLocations(response.data);
                } else {
                    setError("Failed to fetch locations.");
                }
            } catch (error) {
                setError("An error occurred while fetching locations.");
            }
        };
        fetchLocations();
    }, []);

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result); // Set image preview
                setImages(file); // Set image file
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAdd = () => {
        // Clear input fields and set selectedLocation to null for adding a new location
        setSelectedLocation(null);
        setName('');
        setEmail('');
        setMapLink('');
        setAddress('');
        setPhoneNumber('');
        setImages(null);
        setImagePreview(null);
        setIsModalOpen(true);
    };


    const handleEdit = (location) => {
        setSelectedLocation(location);
        setName(location.name || '');
        setEmail(location.email || '');
        setMapLink(location.map_link || '');
        setAddress(location.address || '');
        setPhoneNumber(location.phone_number || '');
        setImagePreview(location.image || null); // Set preview of the existing image
        setIsModalOpen(true);
    };

    const handleDelete = async (locationId) => {
        // Show a SweetAlert confirmation dialog
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Do you really want to delete this location?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            customClass: {
                popup: 'dark:bg-gray-800 dark:text-white', // Dark background and white text
                title: 'dark:text-white',
                confirmButton: 'dark:bg-blue-500 dark:hover:bg-blue-700 dark:text-white',
                cancelButton: 'dark:bg-red-500 dark:hover:bg-red-700 dark:text-white'
            }
        });

        if (result.isConfirmed) {
            // Proceed with deletion if user confirms
            const response = await deleteLocation(locationId);
            if (response.error) {
                toast.error("Failed to delete location.");
            } else {
                setLocations(locations.filter((location) => location.id !== locationId));
                toast.success("Location deleted successfully!");
            }
        } else {
            // toast.info("Location deletion cancelled.");
        }
    };

    const addNewLocation = async () => {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('email', email);
        formData.append('map_link', mapLink);
        formData.append('address', address);
        formData.append('phone_number', phoneNumber);
        if (images) formData.append('image', images);

        const response = await addLocation(formData);
        if (response.error) {
            toast.error("Failed to add location.");
        } else {
            toast.success("Location added successfully!");
            setLocations([...locations, response.data]);
        }

        setIsModalOpen(false); // Close modal after submit
    };

    const updateLocation = async () => {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('email', email);
        formData.append('map_link', mapLink);
        formData.append('address', address);
        formData.append('phone_number', phoneNumber);
        if (images) formData.append('image', images);

        const response = await editLocation(selectedLocation.id, formData);
        if (response.error) {
            toast.error("Failed to update location.");
        } else {
            toast.success("Location updated successfully!");

            // Update the local state immediately
            setLocations(
                locations.map((location) =>
                    location.id === selectedLocation.id
                        ? { ...location, name, email, map_link: mapLink, address, phone_number: phoneNumber, image: images ? images.name : location.image }
                        : location
                )
            );

            setIsModalOpen(false); // Close modal after submit
        }
    };

    const handleSubmit = () => {
        if (selectedLocation) {
            updateLocation();
        } else {
            addNewLocation();
        }
    };

    return (
        <>
            <div className="mb-4 flex justify-end mt-4">
                <button
                    className="bg-purple-800 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-700"
                    onClick={handleAdd}
                >
                    Add Location
                </button>
            </div>
            <div className="z-20 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 ">
                {locations.map((location) => (
                    <div key={location.id} className="bg-white shadow-md p-4 rounded-lg flex flex-col h-full min-h-[400px]  dark:!bg-navy-700 dark:text-white">
                        <div className="flex-grow">
                            <iframe
                                src={location.map_link}
                                title={`Map of ${location.name}`}
                                width="100%"
                                height="250"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                            ></iframe>
                            <p className="font-bold mt-3">{location.name || "name"}</p>

                            <div className="flex justify-between w-full">
                                <div className="text-left">
                                    <p className="mt-2 text-xs">
                                        {location.address?.split(',').map((line, index) => (
                                            <span key={index}>
                                                {line.trim()}
                                                <br />
                                            </span>
                                        )) || "Address"}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="mt-2 text-xs text-green-600 dark:text-white ">
                                        <p className="mt-2 text-xs">
                                            {location.phone_number?.split(',').map((line, index) => (
                                                <span key={index}>
                                                    {line.trim()}
                                                    <br />
                                                </span>
                                            )) || "Address"}
                                        </p>
                                    </p>
                                    <p className="mt-2 text-xs text-blue-600 dark:text-white">{location.email || "Email"}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between items-center mt-4 pt-4 border-t">
                            <button
                                className="border border-green-600 text-green-600 px-4 py-2 rounded-lg hover:bg-green-600 hover:text-white"
                                onClick={() => handleEdit(location)}
                            >
                                <MdEdit />
                            </button>
                            {/* <label className="inline-flex items-center cursor-pointer mt-3 mb-3 ms-2 me-2">
                                <div className="me-2" style={{ fontSize: "10px" }}>
                                    Do you want to display on Home Page
                                </div>
                                <input
                                    type="checkbox"
                                    checked={location.primary_location || false} 
                                    onChange={() => handleCheckboxChange(trainer.id)}
                                    className="sr-only peer"
                                />
                                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-purple-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                            </label> */}
                            <button
                                className="border border-red-800 text-red-800 px-4 py-2 rounded-lg hover:bg-red-800 hover:text-white"
                                onClick={() => handleDelete(location.id)}
                            >
                                <MdDelete />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 ">
                    <div className="bg-white p-6 rounded-xl w-1/2  dark:!bg-navy-700 dark:text-white">
                        <h2 className="text-lg font-semibold text-gray-700">
                            {selectedLocation ? "Edit Location" : "Add Location"}
                        </h2>

                        <div className="flex gap-4 mb-4">
                            <div className="w-1/2">
                                <label className="block text-sm text-gray-700">Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                                />
                            </div>
                            <div className="w-1/2">
                                <label className="block text-sm text-gray-700">Email</label>
                                <input
                                    type="text"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="w-1/2">
                                <label className="block text-sm text-gray-700">Map Link</label>
                                <textarea
                                    rows={5}
                                    type="text"
                                    value={mapLink}
                                    onChange={(e) => setMapLink(e.target.value)}
                                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                                />
                            </div>
                            <div className="w-1/2">
                                <label className="block text-sm text-gray-700">Address</label>
                                <textarea
                                    rows={5}
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="w-1/2">
                                <label className="block text-sm text-gray-700">Phone Number</label>
                                <input
                                    type="text"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                                /> 
                            </div>
                            <div className="w-1/2">

                                <div className="flex justify-center w-full mb-4">
                                    <div className="text-left me-2">
                                        {imagePreview && <img src={imagePreview} alt="Preview" className="w-20 h-20 object-cover mb-2" />}
                                        <input type="file" onChange={handleImageUpload} accept="image/*" className="hidden" id="file-upload" />
                                    </div>
                                    <div className="text-left ms-2">
                                        <label className="block text-sm text-gray-700">Image</label>
                                        <button onClick={() => document.getElementById("file-upload").click()} className="flex items-center justify-center w-10 p-2 bg-purple-600 text-white rounded-md hover:bg-purple-700">
                                            <MdUpload />
                                        </button>
                                        <p className="mt-2" style={{ fontSize: '10px' }}>The height and width must be 1080 × 1350</p>

                                    </div>


                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 mt-4">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="bg-gray-500 text-white px-4 py-2 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg"
                            >
                                {selectedLocation ? "Update Location" : "Add Location"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer />
        </>
    );
}

export default Location;
